/**
 * Which side of its trigger a popover opens on, and how tall it may be.
 *
 * ## The defect this exists to fix
 *
 * Every popover in the panel was `absolute … mt-1.5` — pinned below its trigger with no
 * alternative. Three of them, and the one an operator hits daily is the rows-per-page
 * select in `ListFooter`, which lives *under a full-height table*: its list opened into
 * the bottom of the window, so the options were off-screen and the control looked broken.
 * `BaseMenu`'s row actions have the same problem on the last rows of any long table.
 *
 * A native `<select>` has never behaved this way. It opens downward when the list fits and
 * flips up when it does not — which is the behaviour being restored here, for controls
 * that are custom listboxes precisely because native ones could not be styled.
 *
 * ## Why it is a pure function in utils/
 *
 * Placement is arithmetic over four numbers, and arithmetic is the part that is wrong in
 * the interesting cases: a trigger half-scrolled out of the viewport, a list taller than
 * the window, a short viewport where neither side fits. Those are three lines to assert
 * here and a fixture nobody writes if the maths lives inside a component — which is the
 * repository's standing rule for a component whose hard part is arithmetic.
 */

export type PopoverSide = 'below' | 'above'

/** Which trigger edge the panel's own edge lines up with. */
export type PopoverAlign = 'start' | 'end'

/** Viewport coordinates, exactly as `getBoundingClientRect()` and `innerHeight` give them. */
export interface PopoverSpace {
  readonly triggerTop: number
  readonly triggerBottom: number
  readonly viewportHeight: number
}

/** The horizontal equivalent. Separate because the two axes are decided independently. */
export interface PopoverWidthSpace {
  readonly triggerLeft: number
  readonly triggerRight: number
  readonly viewportWidth: number
}

export interface PopoverAlignment {
  readonly align: PopoverAlign
  /** What the panel's `max-width` must be to stay inside the viewport. */
  readonly maxWidth: number
}

export interface PopoverPlacement {
  readonly side: PopoverSide
  /**
   * What the panel's `max-height` must be to stay inside the viewport — or **null when
   * the panel fits and needs no cap at all.**
   *
   * Null rather than "the height it already is", which is what this returned first and
   * which put a scrollbar on every short menu. `max-height` under `box-sizing:
   * border-box` constrains the BORDER box, while the measured height arrives from a
   * padding-box metric, so a cap set to the panel's own height is 2px short of it — one
   * pixel of border at the top, one at the bottom. `overflow-y: auto` then shows a
   * scrollbar for exactly those two pixels: measured at 139px of content in a 137px box
   * on a one-item menu.
   *
   * A cap that is only ever equal to the content is a no-op in intent and a defect in
   * effect. When nothing needs constraining, nothing is constrained.
   */
  readonly maxHeight: number | null
}

/** The offset between trigger and panel — `mt-1.5`, kept in step with the template. */
export const POPOVER_GAP = 6

/**
 * Below the point a list stops being a list.
 *
 * When neither side fits, the panel takes the larger one and is allowed to overrun it
 * rather than shrink past this. A 20-pixel panel showing a third of one option is not a
 * smaller version of the control, it is a broken one — and the case only arises on a
 * viewport so short that something has to give.
 */
export const POPOVER_MIN_HEIGHT = 96

/**
 * @param preferredHeight how tall the panel wants to be — its natural or capped height.
 * @param margin breathing room kept against the viewport edge, on top of the gap.
 */
export function placePopover(
  space: PopoverSpace,
  preferredHeight: number,
  margin = 8,
): PopoverPlacement {
  // Clamped at zero: a trigger scrolled past the top of the window has a negative `top`,
  // and a negative "space above" would otherwise win a `>` comparison against a small
  // positive one and flip the panel off the screen entirely.
  const below = Math.max(0, space.viewportHeight - space.triggerBottom - POPOVER_GAP - margin)
  const above = Math.max(0, space.triggerTop - POPOVER_GAP - margin)

  // Downward is the default and stays the default whenever the list genuinely fits. A
  // popover that flipped on the basis of which side merely had MORE room would move
  // around under an operator scrolling a list, which is worse than always opening down.
  if (preferredHeight <= below) return { side: 'below', maxHeight: null }
  if (preferredHeight <= above) return { side: 'above', maxHeight: null }

  // Neither fits: take the roomier side and scroll inside it.
  const side: PopoverSide = above > below ? 'above' : 'below'

  return { side, maxHeight: Math.max(POPOVER_MIN_HEIGHT, side === 'above' ? above : below) }
}


/**
 * Which way a panel WIDER THAN ITS TRIGGER should grow, and how wide it may get.
 *
 * A select panel is no longer locked to its trigger's width: the event filter is a 14 rem
 * field listing labels like "Opened the App Store review link", and matching the trigger
 * truncated every one of them to "Opened the App Store r…" — a filter whose options cannot
 * be told apart. Letting it size to its content fixes that and creates this problem, which
 * is that a content-width panel on a right-hand field runs off the screen.
 *
 * Deliberately a second function rather than more returns from `placePopover`: the axes
 * share nothing but a trigger. Vertical flips because a list is tall and a window is
 * short; horizontal aligns because a panel is wider than the control that opened it. One
 * function answering both would take six numbers to answer two unrelated questions.
 */
export function alignPopover(
  space: PopoverWidthSpace,
  preferredWidth: number,
  margin = 8,
): PopoverAlignment {
  const room = Math.max(0, space.viewportWidth - margin * 2)
  const width = Math.min(preferredWidth, room)

  // Growing rightward from the trigger's left edge is the default — it keeps the panel's
  // text aligned with the control's, which is what makes a long list scannable.
  if (space.triggerLeft + width <= space.viewportWidth - margin) {
    return { align: 'start', maxWidth: room }
  }

  // It would overrun the right edge. Hang it off the trigger's RIGHT edge instead, which
  // is the same move a native menu makes near the edge of a screen.
  if (space.triggerRight - width >= margin) return { align: 'end', maxWidth: room }

  // Wider than the viewport allows either way: pin it to the left edge of the trigger and
  // let `maxWidth` do the cutting, so the panel stays on screen and wraps or truncates
  // inside itself rather than disappearing past the edge.
  return { align: 'start', maxWidth: Math.max(0, space.viewportWidth - space.triggerLeft - margin) }
}
